"use client";

import { BrokerAvatar } from "@/components/brokers/broker-avatar";
import { mediaQuery } from "@/lib/media-query";
import type { Agent } from "@/lib/types";
import { buildAgentProfileUrl } from "@/lib/url";
import { formatDistanceToNow } from "date-fns";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useMediaQuery } from "usehooks-ts";

export type ListingAgentCardProps = {
  agent: Agent;
};

export function ListingAgentCard({ agent }: ListingAgentCardProps) {
  const t = useTranslations();

  const name = agent?.name ?? agent?.email ?? "";

  const isNotMobile = useMediaQuery(mediaQuery.sm);

  const joinedAgo = useMemo(
    () =>
      formatDistanceToNow(new Date(agent.createdAt), {
        addSuffix: true,
      }),
    [agent.createdAt],
  );

  const activeListingsCount =
    (agent as Agent & { _count?: { listings?: number } })._count?.listings ?? null;

  const subtitleParts = useMemo(() => {
    const parts: string[] = [];

    if (agent.isVerified) {
      parts.push(t("saas.listing.agentPublic.description.verifiedBroker"));
    }

    parts.push(
      t("saas.listing.agentPublic.description.joined", { when: joinedAgo }),
    );

    if (activeListingsCount && activeListingsCount > 0) {
      parts.push(
        `${activeListingsCount} active listing${activeListingsCount > 1 ? "s" : ""}`,
      );
    }

    return parts;
  }, [agent, joinedAgo, activeListingsCount, t]);

  const profileHref = buildAgentProfileUrl(agent) ?? "/business-brokers";

  return (
    <a
      href={profileHref}
      target={isNotMobile ? "_blank" : undefined}
      rel={isNotMobile ? "noreferrer" : undefined}
      className="group"
    >
      <article className="flex gap-4 items-start md:items-center">
        <BrokerAvatar
          id={agent.id}
          name={name}
          image={agent.image}
          isVerified={agent.isVerified}
          size="sm"
        />

        <div className="flex-1 flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground group-hover:underline truncate min-w-0">
              {t.rich("saas.listing.agentPublic.listedBy", {
                name,
                strong: (children) => <strong>{children}</strong>,
              })}
            </span>

            {agent.isVerified && (
              <span className="inline-flex items-center gap-1 shrink-0 whitespace-nowrap rounded-full bg-[#E8F9EF] text-[#00803A] text-xs font-medium px-2 py-0.5">
                Verified ✓
              </span>
            )}
          </div>

          <div className="text-xs sm:text-sm text-gray-500">
            {subtitleParts.join(" · ")}
          </div>
        </div>
      </article>
    </a>
  );
}
